import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ShieldAlert, Flame, MessageSquareReply, Inbox, Brain, ChevronRight, HeartHandshake, Anchor, Scale } from "lucide-react";
import Layout from "@/components/Layout";
import { api, vibrate } from "@/lib/api";
import { listContainer, listItem } from "@/lib/motion";
import { SkeletonList } from "@/components/Skeleton";

const VLABEL = {
  red_flags: { label: "Red Flags", icon: ShieldAlert },
  rizz_score: { label: "Rizz Score", icon: Flame },
  reply_suggestions: { label: "Réponses", icon: MessageSquareReply },
  attachment_style: { label: "Attachement", icon: Anchor },
  compatibility: { label: "Compatibilité", icon: HeartHandshake },
};

const SRC = { whatsapp: "WhatsApp", screenshot: "Screenshots", paste: "Collage" };

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
}

function toxColor(t) {
  if (t == null) return "#a78bfa";
  if (t >= 66) return "#f87171";
  if (t >= 40) return "#fbbf24";
  return "#34d399";
}

const Spinner = () => (
  <div className="flex justify-center py-12">
    <div className="w-8 h-8 rounded-full border-2 border-rose-500 border-t-transparent animate-spin" />
  </div>
);

export default function History() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("analyses");
  const [items, setItems] = useState(null);
  const [profiles, setProfiles] = useState(null);

  useEffect(() => {
    api.get("/analyses").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/profile/list/mine").then((r) => setProfiles(r.data?.profiles || [])).catch(() => setProfiles([]));
  }, []);

  const tabs = [
    { k: "analyses", label: "Analyses", count: items?.length },
    { k: "profiles", label: "Profils psy", count: profiles?.length },
  ];

  // Relationships that have BOTH profiles (me + them) → eligible for the compare shortcut.
  const comparable = React.useMemo(() => {
    const byRel = {};
    (profiles || []).forEach((p) => {
      byRel[p.relationship_id] = byRel[p.relationship_id] || new Set();
      byRel[p.relationship_id].add(p.target);
    });
    return new Set(Object.entries(byRel).filter(([, s]) => s.has("me") && s.has("them")).map(([r]) => r));
  }, [profiles]);

  return (
    <Layout>
      <button
        data-testid="history-back"
        onClick={() => navigate("/app")}
        className="flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-5 transition-colors"
      >
        <ArrowLeft size={16} /> Accueil
      </button>

      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Ton journal</p>
      <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1.5 mb-5">
        <span className="text-gradient">Historique</span>
      </h1>

      {/* Segmented tabs */}
      <div className="glass-card rounded-full p-1 grid grid-cols-2 mb-6" data-testid="history-tabs">
        {tabs.map((t) => {
          const active = tab === t.k;
          return (
            <button
              key={t.k}
              data-testid={`history-tab-${t.k}`}
              onClick={() => { vibrate(); setTab(t.k); }}
              className="relative py-2.5 rounded-full text-sm font-semibold transition-colors"
            >
              {active && (
                <motion.span
                  layoutId="history-tab-pill"
                  className="absolute inset-0 rounded-full bg-gradient-to-r from-rose-600 to-purple-600 shadow-[0_0_20px_rgba(225,29,72,0.35)]"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
              <span className={`relative flex items-center justify-center gap-1.5 ${active ? "text-white" : "text-slate-400"}`}>
                {t.k === "profiles" && <Brain size={14} />}
                {t.label}
                {typeof t.count === "number" && (
                  <span className={`text-[10px] font-bold rounded-full px-1.5 py-px ${active ? "bg-white/25" : "bg-white/10 text-slate-400"}`}>
                    {t.count}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {tab === "analyses" ? (
          <motion.div key="analyses" initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.18 }}>
            {items === null ? <SkeletonList count={4} testid="history-skeleton" /> : items.length === 0 ? (
              <EmptyState
                testid="history-empty-cta"
                text="Aucune analyse pour l'instant."
                cta="Lancer une analyse"
                onClick={() => navigate("/import")}
              />
            ) : (
              <motion.div variants={listContainer} initial="hidden" animate="show" className="space-y-3" data-testid="history-list">
                {items.map((a, i) => {
                  const meta = VLABEL[a.vertical] || VLABEL.red_flags;
                  const Icon = meta.icon;
                  const badge = a.grade || (typeof a.score === "number" ? a.score : null);
                  return (
                    <motion.button
                      key={a.analysis_id}
                      data-testid={`history-item-${a.analysis_id}`}
                      variants={listItem}
                      onClick={() => { vibrate(); navigate(`/analysis/${a.analysis_id}`); }}
                      className="w-full flex items-center gap-3 rounded-2xl glass-card p-4 text-left hover:border-white/20 transition-colors active:scale-[0.99]"
                    >
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-rose-500/20 to-purple-500/20 flex items-center justify-center text-rose-400 shrink-0">
                        <Icon size={19} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm truncate">{a.contact_name || "Conversation"}</p>
                        <p className="text-xs text-slate-500">
                          {meta.label} · {SRC[a.source] || a.source} · {fmtDate(a.created_at)}
                        </p>
                      </div>
                      {badge !== null && (
                        <span className="font-display text-lg font-extrabold text-rose-400 shrink-0">{badge}</span>
                      )}
                    </motion.button>
                  );
                })}
              </motion.div>
            )}
          </motion.div>
        ) : (
          <motion.div key="profiles" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }} transition={{ duration: 0.18 }}>
            {profiles === null ? <SkeletonList count={3} testid="profiles-skeleton" /> : profiles.length === 0 ? (
              <EmptyState
                testid="profiles-empty-cta"
                icon={Brain}
                text="Aucun profil psychologique généré. Ouvre une relation et génère son profil comportemental."
                cta="Voir mes relations"
                onClick={() => navigate("/app")}
              />
            ) : (
              <motion.div variants={listContainer} initial="hidden" animate="show" className="space-y-3" data-testid="profiles-list">
                {profiles.map((p, i) => {
                  const c = toxColor(p.toxicity);
                  const canCompare = comparable.has(p.relationship_id);
                  return (
                    <motion.button
                      key={p.analysis_id}
                      data-testid={`profile-item-${p.analysis_id}`}
                      variants={listItem}
                      onClick={() => { vibrate(); navigate(`/profile/${p.relationship_id}${p.target === "me" ? "?target=me" : ""}`); }}
                      className="w-full flex items-center gap-3 rounded-2xl glass-card p-4 text-left hover:border-purple-400/30 transition-colors active:scale-[0.99]"
                      style={{ borderColor: `${c}33` }}
                    >
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0"
                        style={{ background: `radial-gradient(120% 120% at 30% 20%, ${c}33 0%, rgba(255,255,255,0.04) 70%)` }}
                      >
                        {p.verdict_emoji}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <p className="font-semibold text-sm truncate">
                            {p.target === "me" ? "Moi" : p.contact_name}
                          </p>
                          {p.target === "me" && <span className="text-[9px] font-bold uppercase tracking-wide text-purple-300 bg-purple-500/15 rounded-full px-1.5 py-px">toi</span>}
                        </div>
                        <p className="text-xs text-slate-300 truncate mt-0.5">{p.verdict_title || p.dominant_archetype || "Profil"}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {p.toxicity != null && (
                            <span className="text-[10px] font-bold" style={{ color: c }}>🌡️ {p.toxicity}°</span>
                          )}
                          <span className="text-[10px] text-slate-500">{fmtDate(p.created_at)}</span>
                        </div>
                      </div>
                      {canCompare ? (
                        <span
                          data-testid={`profile-compare-${p.relationship_id}`}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); vibrate(); navigate(`/profile/${p.relationship_id}?compare=1`); }}
                          className="shrink-0 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-rose-500/20 to-purple-500/20 border border-purple-400/30 text-purple-200 text-[11px] font-bold px-2.5 py-1.5 hover:from-rose-500/30 hover:to-purple-500/30 transition-colors"
                        >
                          <Scale size={12} /> Comparer
                        </span>
                      ) : (
                        <ChevronRight size={18} className="text-slate-600 shrink-0" />
                      )}
                    </motion.button>
                  );
                })}
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
}

function EmptyState({ testid, icon: Icon = Inbox, text, cta, onClick }) {
  return (
    <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center glass-card">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-500/15 to-purple-500/15 flex items-center justify-center text-slate-400 mx-auto mb-4">
        <Icon size={26} />
      </div>
      <p className="text-sm text-slate-400 max-w-xs mx-auto">{text}</p>
      <button
        data-testid={testid}
        onClick={() => { vibrate(); onClick(); }}
        className="mt-5 rounded-full bg-gradient-to-r from-rose-600 to-purple-600 text-white font-semibold px-6 py-2.5 text-sm active:scale-95 transition-transform shadow-[0_0_20px_rgba(225,29,72,0.3)]"
      >
        {cta}
      </button>
    </div>
  );
}
